import * as Blockly from 'blockly/core';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import { useBlockModalFunctionStore } from '@/stores/useBlockModalFunctionStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 118;
const PADDING_H = 12;

export class FieldFunction extends Blockly.Field<string> {
    private labelEl_: SVGTextElement | null = null;

    static override fromJson(options: Blockly.FieldConfig & { fnName?: string }): FieldFunction {
        return new FieldFunction(options.fnName ?? '作成した関数');
    }

    constructor(fnName: string) {
        super(fnName);
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: PADDING_H,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': 'normal',
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#7FD700';
        const isPlaceholder = this.getValue() === '作成した関数';
        const fillColour = isPlaceholder ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private calcWidth_(): number {
        const text = this.getValue() ?? '';
        let w = 0;
        for (const char of text) {
            w += char.charCodeAt(0) > 127 ? 14 : 8;
        }
        const isPlaceholder = text === '作成した関数';
        return Math.max(isPlaceholder ? FIELD_MIN_WIDTH : 24, PADDING_H + w + PADDING_H);
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_ || !this.labelEl_) return;
        this.labelEl_.textContent = this.getValue() ?? '';
        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);

        requestAnimationFrame(() => {
            if (!this.labelEl_ || !this.borderRect_) return;
            try {
                const bbox = this.labelEl_.getBBox();
                if (bbox.width > 0) {
                    const text = this.getValue() ?? '';
                    const isPlaceholder = text === '作成した関数';
                    const realWidth = Math.max(
                        isPlaceholder ? FIELD_MIN_WIDTH : 24,
                        PADDING_H + bbox.width + PADDING_H,
                    );
                    this.borderRect_.setAttribute('width', String(realWidth));
                    this.size_ = new Blockly.utils.Size(realWidth, FIELD_HEIGHT);
                    (this.getSourceBlock() as Blockly.BlockSvg)?.render();
                }
            } catch {}
        });

        this.applyStyle_();
    }

    public setFnId(id: string, name: string): void {
        this.setValue(name);
        const block = this.getSourceBlock();
        if (!block) return;

        const fn = useBlockFunctionStore.getState().functions.find((f) => f.id === id);
        (block as any)._fnId = id;
        (block as any)._fnName = name;
        (block as any)._params = fn?.params ?? [];

        const xml = Blockly.utils.xml.createElement('mutation');
        xml.setAttribute('fn_id', id);
        xml.setAttribute('fn_name', name);
        xml.setAttribute('params', JSON.stringify(fn?.params ?? []));
        (block as any).domToMutation(xml);
        (block as Blockly.BlockSvg).render();

        Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', name));
    }

    public setFnName(name: string): void {
        this.setValue(name);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', name));
        }
    }

    override doValueUpdate_(newValue: string): void {
        super.doValueUpdate_(newValue);
        if (this.fieldGroup_) this.updateDisplay_();
    }

    override getSize(): Blockly.utils.Size {
        if (this.borderRect_) {
            const w = parseFloat(this.borderRect_.getAttribute('width') ?? '0');
            if (w > 0) {
                this.size_ = new Blockly.utils.Size(w, FIELD_HEIGHT);
                return this.size_;
            }
        }
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const ws = this.getSourceBlock()?.workspace as Blockly.WorkspaceSvg | undefined;
        if (ws) useBlockModalFunctionStore.getState().setWorkspace(ws);
        useBlockModalFunctionStore.getState().setFieldRef(this);
        useBlockModalStore.getState().openModal('func');
    }
}
